import axiosInstance from "../config/axiosConfig";
import useConfigStore from "../store/configStore";
import useTokenStore from "../store/tokenStore";

export interface FailedFileInfo {
  status: number;
  name: string;
  message: string;
}

export interface FileUploadResponse {
  status: number;
  message: string;
  collection_names: string[];
  summary: {
    total_files: number;
    processed_files: number;
    failed_files: number;
  };
  files: {
    file_id: string;
    file_name: string;
    url: string;
    collection_name: string | null;
    status: "success" | "failed";
  }[];
  failed_files: FailedFileInfo[];
}

/**
 * Upload files to the server
 */
export const uploadFiles = async (
  files: File[],isWebEnabled?: boolean,
  projectId?: string | null
): Promise<FileUploadResponse> => {
  const config = useConfigStore.getState().getConfig();
  if (!config) {
    throw new Error("Config not initialized");
  }

  const endpoint = useConfigStore.getState().getEndpoint("uploadFile");
  if (!endpoint) {
    throw new Error("Upload endpoint not configured");
  }

  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

   const normalizedProjectId = projectId ?? "";

   if (isWebEnabled) {
    // for web
    formData.append("mode", "web");
    formData.append("agent_type", "default");
  } else if (!normalizedProjectId) {
    // No projectId
    formData.append("mode", "school");
    formData.append("agent_type", "default");
  } else {
    // projectId present
    formData.append("mode", "school");
    formData.append("agent_type", normalizedProjectId);
  }
  

  const token = useTokenStore.getState().token;
  const response = await axiosInstance.post(endpoint, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(token && { "Authorization": `Token ${token}` }),
    },
  });

  return response.data;
};

/**
 * Allowed file extensions for upload
 */
export const ALLOWED_FILE_EXTENSIONS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'txt',
  'xls',
  'xlsx',
  'doc',
  'docx',
  'pptx'
];

/**
 * Get file extension from filename (lowercase)
 */
const getFileExtension = (filename: string): string => {
  if (!filename) return '';
  
  const lastDot = filename.lastIndexOf('.');
  
  // No extension found or dot is at the start (hidden file)
  if (lastDot === -1 || lastDot === 0) return '';
  
  return filename.slice(lastDot + 1).toLowerCase();
};

/**
 * Check if a file has an allowed extension
 */
export const isFileTypeAllowed = (file: File): boolean => {
  const extension = getFileExtension(file.name);
  return ALLOWED_FILE_EXTENSIONS.includes(extension);
};

/**
 * Validate files before upload
 */
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateFiles = (
  files: File[],
  maxFiles: number = 5,
  maxTotalSizeMB: number = 25
): FileValidationResult => {
  // Check file count
  if (files.length > maxFiles) {
    return {
      isValid: false,
      error: `You can only upload up to ${maxFiles} files at a time.`,
    };
  }

  // Check file types
  const invalidFiles = files.filter((file) => !isFileTypeAllowed(file));
  if (invalidFiles.length > 0) {
    const invalidNames = invalidFiles.map((f) => f.name).join(', ');
    const allowedTypes = ALLOWED_FILE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(', ');
    return {
      isValid: false,
      error: `Invalid file type(s): ${invalidNames}. Allowed types: ${allowedTypes}`,
    };
  }

  // Check total size
  const totalSizeBytes = files.reduce((sum, file) => sum + file.size, 0);
  const totalSizeMB = totalSizeBytes / (1024 * 1024);

  if (totalSizeMB > maxTotalSizeMB) {
    return {
      isValid: false,
      error: `Total file size must be less than ${maxTotalSizeMB} MB. Current size: ${totalSizeMB.toFixed(2)} MB`,
    };
  }

  return { isValid: true };
};

/**
 * Delete file by collection name
 */
export interface FileDeleteResponse {
  status: number;
  message: string;
  deleted_count: number;
  collection_name: string;
  vector_deleted: boolean;
  deleted_collections: string[];
  failed_collections: string[];
}

export const deleteFile = async (
  collection_name: string
): Promise<FileDeleteResponse> => {
  const config = useConfigStore.getState().getConfig();
  if (!config) {
    throw new Error("Config not initialized");
  }

  const endpoint = useConfigStore.getState().getEndpoint("deleteFile");
  if (!endpoint) {
    throw new Error("Delete endpoint not configured");
  }

  const token = useTokenStore.getState().token;
  // Pass collection_name as query parameter
  const response = await axiosInstance.delete(endpoint, {
    params: {
      collection_name: collection_name,
    },
    headers: {
      ...(token && { "Authorization": `Token ${token}` }),
    },
  });

  return response.data;
};

